import { useEffect, useRef, useState } from "react";

import {
  flattenConversationPages,
  useChatConversations,
  useFindOrCreateConversation,
  useSearchDrivers,
  useSessionUser,
  useTimezone,
} from "@web-app/api";
import { Avatar, Input } from "@web-app/components";

import PushNotificationPrompt from "./PushNotificationPrompt";
import {
  getConversationDisplayImage,
  getConversationDisplayName,
  getLastMessagePreview,
} from "./chatUtils";
import AIIcon from "./icons/AIIcon";
import ChatIcon from "./icons/ChatIcon";
import CubeIcon from "./icons/CubeIcon";
import PhoneIcon from "./icons/PhoneIcon";

interface ConversationListProps {
  onSelectConversation: (conversationId: string) => void;
  selectedConversationId?: string;
}

export default function ConversationList({
  onSelectConversation,
  selectedConversationId,
}: ConversationListProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewChat, setShowNewChat] = useState(false);
  const [driverSearchTerm, setDriverSearchTerm] = useState("");
  const [debouncedDriverSearch, setDebouncedDriverSearch] = useState("");
  const newChatRef = useRef<HTMLDivElement>(null);

  const { data: conversationsData } = useChatConversations();
  const conversations = flattenConversationPages(conversationsData);
  const { data: currentUser } = useSessionUser();
  const { data: driversData, isLoading: isSearchingDrivers } = useSearchDrivers(
    debouncedDriverSearch,
  );
  const drivers = driversData ?? [];
  const { mutateAsync: findOrCreateConversation, isPending: isCreating } =
    useFindOrCreateConversation();
  const { userTimezone, convertToTimezone } = useTimezone();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDriverSearch(driverSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [driverSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        newChatRef.current &&
        !newChatRef.current.contains(event.target as Node)
      ) {
        setShowNewChat(false);
        setDriverSearchTerm("");
      }
    };

    if (showNewChat) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showNewChat]);

  const handleDriverSelect = async (driverId: string) => {
    const conversation = await findOrCreateConversation({
      userIds: [driverId],
    });
    onSelectConversation(conversation.conversation.id);
    setShowNewChat(false);
    setDriverSearchTerm("");
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!currentUser) return true;
    if (!searchTerm.trim()) return true;

    const search = searchTerm.toLowerCase();
    const displayName = getConversationDisplayName(conv, currentUser.id);
    const nameMatch = displayName.toLowerCase().includes(search);
    const orderIdMatch = conv.orderDisplayId?.toLowerCase().includes(search);

    return nameMatch || orderIdMatch;
  });

  if (!currentUser) return null;

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      <div className="p-3 lg:p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg lg:text-xl font-semibold text-gray-900">
          Inbox
        </h2>
        <div className="relative" ref={newChatRef}>
          <button
            onClick={() => setShowNewChat(!showNewChat)}
            className="flex items-center gap-1 text-sm text-gray-900 hover:bg-gray-100 rounded-md px-2 py-1 transition-colors"
          >
            <ChatIcon className="w-5 h-5 lg:w-6 lg:h-6" />
            <span className="hidden sm:inline">New Chat</span>
          </button>
          {showNewChat && (
            <div className="absolute top-full right-0 mt-2 w-64 lg:w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
              <div className="p-2 lg:p-3 border-b border-gray-100">
                <Input
                  type="search"
                  placeholder="Search drivers..."
                  value={driverSearchTerm}
                  onChange={setDriverSearchTerm}
                  autoFocus
                />
              </div>
              <div className="max-h-56 lg:max-h-64 overflow-y-auto">
                {isSearchingDrivers ? (
                  <div className="p-3 lg:p-4 text-center text-gray-400 text-xs lg:text-sm">
                    <div className="animate-spin rounded-full h-4 w-4 lg:h-5 lg:w-5 border-2 border-gray-400 border-t-transparent mx-auto mb-2" />
                    Searching...
                  </div>
                ) : drivers.length === 0 ? (
                  <div className="p-3 lg:p-4 text-center text-gray-400 text-xs lg:text-sm">
                    {driverSearchTerm
                      ? "No drivers found"
                      : "Type to search drivers"}
                  </div>
                ) : (
                  drivers.map((driver) => (
                    <button
                      key={driver.id}
                      onClick={() => handleDriverSelect(driver.id)}
                      disabled={isCreating}
                      className="w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-3 hover:bg-gray-50 text-left disabled:opacity-50"
                    >
                      <Avatar
                        image={driver.image || ""}
                        name={driver.name}
                        size={7}
                      />
                      <div>
                        <p className="text-xs font-medium text-gray-900">
                          {driver.name}
                        </p>
                        <p className="text-[10px] lg:text-xs text-gray-500">
                          {driver.phone}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="p-3 lg:p-4">
        <Input
          type="search"
          placeholder="Search by name or order ID"
          value={searchTerm}
          onChange={setSearchTerm}
        />
      </div>

      <PushNotificationPrompt />

      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="p-6 lg:p-8 text-center text-gray-400 text-xs lg:text-sm">
            {searchTerm.trim()
              ? "No conversations found"
              : "No conversations yet"}
          </div>
        ) : (
          filteredConversations.map((conv) => {
            const isAIConversation = conv.participantsWithDetails?.some(
              (p) => p.role === "AI_AGENT",
            );
            const displayName = isAIConversation
              ? "FULFLLD AI"
              : currentUser
                ? getConversationDisplayName(conv, currentUser.id)
                : "Unknown";
            const displayImage = currentUser
              ? getConversationDisplayImage(conv, currentUser.id)
              : "";
            const isSelected = conv.conversation.id === selectedConversationId;

            return (
              <div
                key={conv.conversation.id}
                onClick={() => onSelectConversation(conv.conversation.id)}
                className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                  isSelected
                    ? "bg-blue-50 border-l-4 border-l-blue-500"
                    : "hover:bg-gray-50"
                }`}
              >
                {isAIConversation ? (
                  <AIIcon className="w-9 h-9 lg:w-10 lg:h-10 flex-shrink-0" />
                ) : (
                  <Avatar image={displayImage} name={displayName} size={9} />
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5 lg:mb-1">
                    <div className="flex items-center gap-1.5 lg:gap-2 min-w-0">
                      <h3 className="text-sm lg:text-base font-semibold text-gray-900 truncate">
                        {displayName}
                      </h3>
                      {(conv.orderExternalId || conv.orderDisplayId) && (
                        <span className="hidden sm:flex items-center gap-1 text-xs text-gray-500 flex-shrink-0 bg-[#EFF6FF] px-1.5 lg:px-2 py-0.5 rounded-lg">
                          {conv.orderExternalId ? (
                            <>
                              {conv.orderAppAvatarUrl && (
                                <img
                                  src={conv.orderAppAvatarUrl}
                                  className="h-3.5 w-3.5 lg:h-4 lg:w-4"
                                  alt=""
                                />
                              )}
                              {conv.orderExternalId}
                            </>
                          ) : (
                            <>
                              <CubeIcon className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                              {conv.orderDisplayId}
                            </>
                          )}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] lg:text-xs text-gray-500 ml-1.5 lg:ml-2 flex-shrink-0">
                      {conv.lastMessage
                        ? convertToTimezone(
                            new Date(conv.lastMessage.createdAt),
                            userTimezone,
                          ).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] lg:text-xs text-gray-500 truncate flex items-center gap-1">
                      {conv.lastMessage?.messageType === "CALL" && (
                        <PhoneIcon
                          className={`w-3 h-3 lg:w-4 lg:h-4 flex-shrink-0 ${conv.lastMessage.callStatus === "ONGOING" ? "text-green-600" : "text-gray-500"}`}
                        />
                      )}
                      {getLastMessagePreview(conv.lastMessage, currentUser.id)}
                    </p>
                    {conv.unreadCount > 0 && (
                      <div className="w-4 h-4 lg:w-5 lg:h-5 bg-gray-900 rounded-full flex items-center justify-center ml-1.5 lg:ml-2 flex-shrink-0">
                        <span className="text-[10px] lg:text-xs text-white font-semibold">
                          {conv.unreadCount > 9 ? "9+" : conv.unreadCount}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
